"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Search, ArrowRight, FileText, Loader2 } from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@repo/ui/components/command";
import { searchPostsAction, type GlobalSearchHit } from "./search-action";

interface GlobalSearchCommandProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentLanguage: "en" | "mm";
}

/** Build a stable post URL from a search hit. */
function hitHref(hit: GlobalSearchHit): string {
  if (hit.url && hit.url.startsWith("/")) return hit.url;
  if (hit.slug) {
    return hit.postTypeSlug
      ? `/post/${hit.postTypeSlug}/${hit.slug}`
      : `/post/${hit.slug}`;
  }
  return "/search";
}

/**
 * Epona global search palette.
 *
 * Owns the Cmd/Ctrl+K global shortcut (toggles the dialog anywhere on
 * the page) and controlled by a parent-held `open` boolean so the
 * header's search button can open it too. Debounced (~250 ms) queries
 * are answered by the `searchPostsAction` server action — no
 * cross-origin fetch from the browser.
 *
 * Selecting a result routes to its post URL; an always-present
 * "Search all results" row (and pressing Enter with nothing selected)
 * navigates to the full `/search?q=` results page.
 */
export function GlobalSearchCommand({
  open,
  onOpenChange,
  currentLanguage,
}: GlobalSearchCommandProps) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GlobalSearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reqIdRef = useRef(0);

  const isMM = currentLanguage === "mm";
  const placeholder = isMM
    ? "ဆောင်းပါး၊ စာမျက်နှာများ ရှာဖွေရန်..."
    : "Search posts, pages…";
  const allResultsLabel = isMM
    ? `“${query.trim()}” အတွက် ရလဒ်အားလုံး ရှာရန်`
    : `Search all results for “${query.trim()}”`;

  // ── Global Cmd/Ctrl+K shortcut — toggles the palette anywhere.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  // ── Reset transient state whenever the dialog closes.
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setLoading(false);
    }
  }, [open]);

  // ── Debounced server-action search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const reqId = ++reqIdRef.current;
      try {
        const hits = await searchPostsAction(q, currentLanguage, 8);
        // Ignore out-of-order responses.
        if (reqId === reqIdRef.current) setResults(hits);
      } catch {
        if (reqId === reqIdRef.current) setResults([]);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, currentLanguage]);

  const goToAll = useCallback(() => {
    const q = query.trim();
    onOpenChange(false);
    router.push(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  }, [query, onOpenChange, router]);

  const goToHit = useCallback(
    (hit: GlobalSearchHit) => {
      onOpenChange(false);
      router.push(hitHref(hit));
    },
    [onOpenChange, router],
  );

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      shouldFilter={false}
    >
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={placeholder}
        onKeyDown={(e) => {
          // Enter with nothing actionable falls through to full search.
          if (e.key === "Enter" && results.length === 0) {
            e.preventDefault();
            goToAll();
          }
        }}
      />
      <CommandList>
        {loading && (
          <div className="flex items-center gap-2 px-4 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {isMM ? "ရှာဖွေနေသည်..." : "Searching…"}
          </div>
        )}

        {!loading && query.trim() && results.length === 0 && (
          <CommandEmpty>
            {isMM ? "ရလဒ် မတွေ့ပါ" : "No results found."}
          </CommandEmpty>
        )}

        {results.length > 0 && (
          <CommandGroup heading={isMM ? "ရလဒ်များ" : "Results"}>
            {results.map((hit) => (
              <CommandItem
                key={hit._id}
                value={hit._id}
                onSelect={() => goToHit(hit)}
                className="cursor-pointer gap-2 aria-selected:bg-[color:var(--color-accent)]/10 aria-selected:text-[color:var(--color-primary)]"
              >
                <FileText
                  className="h-4 w-4 flex-shrink-0"
                  style={{ color: "var(--color-accent)" }}
                />
                <span className="min-w-0 flex-1 truncate">{hit.title}</span>
                {hit.typeLabel && (
                  <span className="ml-auto flex-shrink-0 rounded-full border border-border px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                    {hit.typeLabel}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {query.trim() && (
          <CommandGroup>
            <CommandItem
              value="__all-results__"
              onSelect={goToAll}
              className="cursor-pointer gap-2 text-[color:var(--color-primary)] aria-selected:bg-[color:var(--color-accent)]/10"
            >
              <Search className="h-4 w-4 flex-shrink-0" />
              <span className="min-w-0 flex-1 truncate">{allResultsLabel}</span>
              <ArrowRight className="ml-auto h-4 w-4 flex-shrink-0 opacity-70" />
            </CommandItem>
          </CommandGroup>
        )}

        {!query.trim() && (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            {isMM
              ? "ရှာဖွေရန် စကားလုံး ရိုက်ထည့်ပါ"
              : "Type to search across the site."}
          </div>
        )}
      </CommandList>
    </CommandDialog>
  );
}

export default GlobalSearchCommand;
